/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Transforms raw `HttpErrorResponse` bodies into the application-internal
 * `NormalisedApiError` model.
 *
 * This service is the single point of Xcelerator contract knowledge in the webapp.
 * It is called exclusively by `ErrorResponseInterceptor` — no other consumer
 * should depend on it directly.
 *
 * Supported shapes:
 * - **Xcelerator**: `{ errors: SiemensErrorObject[] }` — mapped field-by-field
 * - **Legacy validation array**: `{ message: [{field, message, code}], error, statusCode }` —
 *   each array entry mapped to a `NormalisedApiError` with `source.pointer`
 * - **Legacy flat**: `{ message: string, error: string }` — wrapped into a single
 *   `NormalisedApiError`
 * - **Unknown / malformed**: returns a safe default `NormalisedApiError`
 *
 * Related Modules:
 * - `NormalisedApiError`      — application-internal error model (api-error.ts)
 * - `ErrorResponseInterceptor` — calls `transform()` for every HTTP error
 * - `GlobalErrorHandlerService` — consumes the resulting `AppError` subclasses
 *
 * @see {@link https://developer.siemens.com/guidelines/api-guidelines/rest/error.html}
 *   Siemens Xcelerator REST API Guidelines v2.5.1 — Error Reporting
 */

import {Injectable} from '@angular/core';
import {HttpStatusCode} from '@angular/common/http';
import {
  ErrorResponse,
  NormalisedApiError,
  SiemensErrorObject,
} from '../models/errors/api-error';

/** HTTP status text lookup for the most common codes. */
const STATUS_TEXT: Readonly<Partial<Record<number, string>>> = {
  [HttpStatusCode.BadRequest]: 'Bad Request',
  [HttpStatusCode.Unauthorized]: 'Unauthorized',
  [HttpStatusCode.Forbidden]: 'Forbidden',
  [HttpStatusCode.NotFound]: 'Not Found',
  [HttpStatusCode.UnprocessableEntity]: 'Unprocessable Entity',
  [HttpStatusCode.InternalServerError]: 'Internal Server Error',
  [HttpStatusCode.BadGateway]: 'Bad Gateway',
  [HttpStatusCode.ServiceUnavailable]: 'Service Unavailable',
  [HttpStatusCode.GatewayTimeout]: 'Gateway Timeout',
};

const DEFAULT_TITLE = 'Unknown Error';
const DEFAULT_DETAIL = 'An unknown error occurred.';
const DEFAULT_STATUS = HttpStatusCode.InternalServerError;

/**
 * Transforms raw HTTP error response bodies into the typed `NormalisedApiError`
 * application model.
 *
 * The service is stateless and has no external dependencies, making it trivial
 * to unit-test in isolation without a `TestBed`.
 */
@Injectable({providedIn: 'root'})
export class ErrorTransformerService {
  /**
   * Transforms the raw body of an `HttpErrorResponse` into a
   * `NormalisedApiError`.
   *
   * Detection order:
   * 1. Xcelerator shape — `body.errors` is a non-empty array
   * 2. Legacy validation array — `body.message` is an array of `{field, message, code}` objects
   * 3. Legacy flat shape — `body` is an object with a `message` or `error` string property
   * 4. Default fallback — anything else (null, undefined, unexpected shape)
   *
   * @param status - The HTTP status code of the response.
   * @param body   - The parsed response body from `HttpErrorResponse.error`.
   * @returns A fully populated `NormalisedApiError` with a non-empty `all`
   *          array. Never throws.
   */
  transform(status: number, body: unknown): NormalisedApiError {
    if (this.isXceleratorShape(body)) {
      return this.mapXcelerator(status, body);
    }

    if (this.isLegacyValidationArrayShape(body)) {
      return this.mapLegacyValidationArray(status, body);
    }

    if (this.isLegacyShape(body)) {
      return this.mapLegacy(status, body);
    }

    return this.buildDefault(status);
  }

  // ─── Xcelerator detection & mapping ─────────────────────────────────────────

  /**
   * Returns `true` when `body` conforms to the Xcelerator `{ errors: [...] }`
   * envelope with at least one entry.
   */
  private isXceleratorShape(body: unknown): body is ErrorResponse {
    return this.hasErrorsArray(body) && body.errors.length > 0;
  }

  /**
   * Returns `true` when `body` is a non-null object that contains a non-empty
   * `errors` property which is an array.
   */
  private hasErrorsArray(
    body: unknown,
  ): body is {errors: SiemensErrorObject[]} {
    return (
      body !== null &&
      typeof body === 'object' &&
      'errors' in body &&
      Array.isArray((body as ErrorResponse).errors)
    );
  }

  /**
   * Maps a Xcelerator `ErrorResponse` to a `NormalisedApiError`.
   *
   * - The primary error is `errors[0]`.
   * - `correlationId` is taken from `errors[0].correlationId`.
   * - `all` contains one `NormalisedApiError` per entry in `errors[]`.
   */
  private mapXcelerator(
    status: number,
    body: ErrorResponse,
  ): NormalisedApiError {
    const entries = body.errors.map((entry) =>
      this.mapSiemensErrorObject(status, entry),
    );

    const primary = entries[0];

    return {
      ...primary,
      all: entries,
    };
  }

  /**
   * Maps a single `SiemensErrorObject` to a `NormalisedApiError` entry.
   * The `all` array is intentionally left empty here — it is populated by
   * `mapXcelerator` after all entries are mapped.
   *
   * All optional fields (`correlationId`, `code`, `source`) are merged
   * simultaneously so that an entry carrying all three retains all three.
   */
  private mapSiemensErrorObject(
    httpStatus: number,
    entry: SiemensErrorObject,
  ): NormalisedApiError {
    return {
      status: entry.status ?? httpStatus,
      title: entry.title,
      detail: entry.detail,
      all: [],
      ...(entry.correlationId !== undefined && {
        correlationId: entry.correlationId,
      }),
      ...(entry.code !== undefined && {code: entry.code}),
      ...(entry.source !== undefined && {source: entry.source}),
    };
  }

  // ─── Legacy validation array shape detection & mapping ──────────────────────

  /**
   * Shape produced by the legacy NestJS validation pipeline:
   * `{ message: [{field: string, message: string, code: string}], error: string, statusCode: number }`
   *
   * Detected by: `body.message` is an array (not a string).
   */
  private isLegacyValidationArrayShape(body: unknown): body is {
    message: Array<{field: string; message: string; code: string}>;
    error: string;
    statusCode?: number;
  } {
    if (body === null || typeof body !== 'object') {
      return false;
    }
    const obj = body as Record<string, unknown>;
    return Array.isArray(obj['message']) && typeof obj['error'] === 'string';
  }

  /**
   * Maps the legacy validation array body to a `NormalisedApiError`.
   *
   * Each `{field, message, code}` entry becomes a `NormalisedApiError` in `all[]`:
   * - `source.pointer` — the field path converted to a format compatible with
   *   `normalizeFieldPath()`. Dot-notation paths (e.g. `"generalProductInformation"`)
   *   are prefixed with `/` and dots replaced by `/` to produce RFC 6901 pointers
   *   (e.g. `"/generalProductInformation"`). Empty `field` strings → no `source`.
   * - `detail` ← `entry.message`
   * - `code`   ← `entry.code`
   *
   * The primary entry (first element) is spread at the root level.
   */
  private mapLegacyValidationArray(
    httpStatus: number,
    body: {
      message: Array<{field: string; message: string; code: string}>;
      error: string;
      statusCode?: number;
    },
  ): NormalisedApiError {
    const resolvedStatus =
      typeof body.statusCode === 'number' ? body.statusCode : httpStatus;
    const title = STATUS_TEXT[resolvedStatus] ?? DEFAULT_TITLE;

    const entries: NormalisedApiError[] = body.message.map((msg) => {
      // Convert dot-notation field path to RFC 6901 pointer so that
      // normalizeFieldPath() in the directive can handle it uniformly.
      const pointer =
        msg.field.length > 0 ? '/' + msg.field.replace(/\./g, '/') : undefined;

      return {
        status: resolvedStatus,
        title,
        detail: msg.message,
        all: [],
        ...(msg.code && {code: msg.code}),
        ...(pointer !== undefined && {source: {pointer}}),
      };
    });

    if (entries.length === 0) {
      return this.buildDefault(resolvedStatus);
    }

    const primary = entries[0];
    return {...primary, all: entries};
  }

  // ─── Legacy flat shape detection & mapping ───────────────────────────────

  /**
   * Returns `true` when `body` looks like a legacy flat error object —
   * an object with at least a `message` or `error` string property.
   */
  private isLegacyShape(body: unknown): body is Record<string, unknown> {
    if (body === null || typeof body !== 'object') {
      return false;
    }
    const obj = body as Record<string, unknown>;
    return (
      typeof obj['message'] === 'string' || typeof obj['error'] === 'string'
    );
  }

  /**
   * Wraps a legacy flat error body into a `NormalisedApiError`.
   *
   * Mapping:
   * - `detail`  ← `body.message` → `body.error` → `DEFAULT_DETAIL`
   * - `title`   ← synthesized from `status` via `STATUS_TEXT`
   * - `status`  ← `body.statusCode` (if numeric) → `status` argument
   */
  private mapLegacy(
    httpStatus: number,
    body: Record<string, unknown>,
  ): NormalisedApiError {
    const detail = this.extractLegacyDetail(body);

    const resolvedStatus =
      typeof body['statusCode'] === 'number' ? body['statusCode'] : httpStatus;

    const entry: NormalisedApiError = {
      status: resolvedStatus,
      title: STATUS_TEXT[resolvedStatus] ?? DEFAULT_TITLE,
      all: [],
      detail,
    };

    return {...entry, all: [entry]};
  }

  // ─── Default fallback ─────────────────────────────────────────────────────

  /**
   * Extracts the human-readable detail string from a legacy flat error body.
   * Prefers `body.message`, falls back to `body.error`, then to `DEFAULT_DETAIL`.
   *
   * @param body - The raw legacy error object.
   * @returns The best available detail string.
   */
  private extractLegacyDetail(body: Record<string, unknown>): string {
    if (typeof body['message'] === 'string') {
      return body['message'];
    }
    if (typeof body['error'] === 'string') {
      return body['error'];
    }
    return DEFAULT_DETAIL;
  }

  /**
   * Returns a safe default `NormalisedApiError` for unexpected or malformed
   * response bodies (null, undefined, primitives, empty objects, etc.).
   */
  private buildDefault(status: number): NormalisedApiError {
    const resolvedStatus =
      typeof status === 'number' && status > 0 ? status : DEFAULT_STATUS;

    const entry: NormalisedApiError = {
      status: resolvedStatus,
      title: STATUS_TEXT[resolvedStatus] ?? DEFAULT_TITLE,
      detail: DEFAULT_DETAIL,
      all: [],
    };

    return {...entry, all: [entry]};
  }
}
